<template>
  <Layout>
    <PageHeader title="Impressão em Massa" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- BARRA DE AÇÕES -->
            <div class="row mb-2 align-items-center">
              <div class="col mb-2">
                <div class="search-box">
                  <div class="position-relative">
                    <input
                      type="text"
                      class="form-control"
                      v-model="filters.search"
                      placeholder="Pesquise por Nome ou E-mail e pressione 'ENTER'"
                      @keyup.enter="loadParticipants"
                    />
                    <i class="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
              <div class="col-auto mb-2">
                <select v-model="filters.group_id" @change="loadParticipants" class="form-control">
                  <option value="">Todos os grupos</option>
                  <option v-for="group in groups" :key="group.id" :value="group.id">{{ group.name }}</option>
                </select>
              </div>
              <div class="col-auto mb-2">
                <button class="btn btn-success mr-2" @click="printSelected" :disabled="selectedParticipants.length === 0 || loading || loadingAll">
                  <i class="bx" :class="loading && loadProgress ? 'bx-loader-alt bx-spin' : 'bx-printer'"></i> Imprimir Selecionados ({{ selectedParticipants.length }})
                </button>
                <button class="btn btn-primary" @click="printAll" :disabled="pagination.total === 0 || loadingAll || loading">
                  <i class="bx" :class="loadingAll ? 'bx-loader-alt bx-spin' : 'bx-printer'"></i> Imprimir Todos ({{ pagination.total }})
                </button>
              </div>
              <!-- Progress de chunking durante impressão em massa -->
              <div v-if="loadProgress" class="col-12 mb-2">
                <div class="text-muted small mb-1">
                  Carregando etiquetas... {{ loadProgress.loaded }}/{{ loadProgress.total }}
                </div>
                <div class="progress" style="height: 6px;">
                  <div
                    class="progress-bar bg-info"
                    :style="{ width: ((loadProgress.loaded / loadProgress.total) * 100) + '%' }"
                  ></div>
                </div>
              </div>
              <div class="col-auto mb-2">
                <router-link to="/badge-builder" class="btn btn-outline-secondary">
                  <i class="bx bx-arrow-back"></i> Voltar
                </router-link>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-4">
              <b-spinner variant="primary"></b-spinner>
              <p class="text-muted mt-2">Carregando participantes...</p>
            </div>

            <!-- Template warning -->
            <div v-else-if="!defaultTemplate" class="alert alert-warning d-flex align-items-center">
              <i class="bx bx-error-circle me-2 fs-4"></i>
              <div>
                <strong>Nenhum template padrão configurado!</strong>
                <p class="mb-0">Configure um template como padrão no <router-link to="/badge-builder">Badge Builder</router-link> para poder imprimir etiquetas.</p>
              </div>
            </div>

            <!-- TABELA -->
            <div v-else-if="participants.length > 0">
              <table class="table table-centered table-hover">
                <thead>
                  <tr>
                    <th style="width: 40px;">
                      <input type="checkbox" v-model="selectAll" @change="toggleSelectAll" />
                    </th>
                    <th>Nome</th>
                    <th>Grupo</th>
                    <th style="width: 80px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="participant in participants"
                    :key="participant.id"
                    class="link"
                    :class="{ 'table-active': isSelected(participant.id) }"
                    @click="toggleSelection(participant.id)"
                  >
                    <td @click.stop>
                      <input type="checkbox" :checked="isSelected(participant.id)" @click.stop="toggleSelection(participant.id)" />
                    </td>
                    <td>
                      <h5 class="font-size-14 mb-0">{{ participant.name }}</h5>
                      <p class="text-muted mb-0">{{ participant.email }}</p>
                    </td>
                    <td>
                      <span class="badge badge-soft-secondary font-size-12">{{ getGroupName(participant) }}</span>
                    </td>
                    <td @click.stop>
                      <button class="btn btn-sm btn-primary" @click="printSingle(participant)" :disabled="printingId === participant.id" title="Imprimir">
                        <i v-if="printingId === participant.id" class="bx bx-loader-alt bx-spin"></i>
                        <i v-else class="bx bx-printer"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- PAGINAÇÃO -->
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  Total de registros: <b>{{ pagination.total }}</b>
                </div>
                <div class="dataTables_paginate paging_simple_numbers">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="pagination.currentPage" :total-rows="pagination.total" :per-page="pagination.perPage" @change="changePage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Empty -->
            <div v-else class="text-center py-4">
              <i class="bx bx-user-x display-4 text-muted"></i>
              <h5 class="mt-3">Nenhum participante encontrado</h5>
              <p class="text-muted mb-0">Não há participantes confirmados com os filtros selecionados</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import { successMessage, errorMessage } from '@/components/alertsMessages'
import * as badgeServices from './services'
import * as participantServices from '@/pages/participants/services'
import http from '@/http'

export default {
  name: 'BadgePrintQueue',
  components: {
    Layout,
    PageHeader
  },
  data: function () {
    return {
      items: [
        { text: 'Eventfy', to: '/' },
        { text: 'Badge Builder', to: '/badge-builder' },
        { text: 'Impressão em Massa', active: true }
      ],
      loading: false,
      loadingAll: false,
      printingId: null,
      // Progresso da impressão em massa (chunking de printBulk)
      // null quando idle; { loaded, total } durante o carregamento.
      loadProgress: null,
      participants: [],
      groups: [],
      selectedParticipants: [],
      selectAll: false,
      defaultTemplate: null,
      filters: {
        search: '',
        group_id: ''
      },
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20
      },
      searchTimeout: null
    }
  },
  computed: {
    visiblePages: function () {
      var pages = []
      var start = Math.max(1, this.pagination.currentPage - 2)
      var end = Math.min(this.pagination.lastPage, this.pagination.currentPage + 2)
      for (var i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  mounted: function () {
    this.loadDefaultTemplate()
    this.loadGroups()
    this.loadParticipants()
  },
  methods: {
    loadDefaultTemplate: function () {
      var self = this
      badgeServices.getDefaultTemplate()
        .then(function (response) {
          self.defaultTemplate = response.data || response
        })
        .catch(function () {
          self.defaultTemplate = null
        })
    },

    loadGroups: function () {
      var self = this
      http.get('/admin/groups')
        .then(function (response) {
          self.groups = response.data.data || response.data || []
        })
        .catch(function () {
          self.groups = []
        })
    },

    loadParticipants: function () {
      var self = this
      self.loading = true
      self.selectedParticipants = []
      self.selectAll = false

      var params = {
        status: 3, // Confirmados
        page: self.pagination.currentPage,
        limit: self.pagination.perPage,
        include: 'group'
      }

      if (self.filters.search) {
        params.search = self.filters.search
      }
      if (self.filters.group_id) {
        params.group_id = self.filters.group_id
      }

      participantServices.list(params)
        .then(function (response) {
          self.participants = response.data.data || []
          if (response.data.meta && response.data.meta.pagination) {
            self.pagination.currentPage = response.data.meta.pagination.current_page || 1
            self.pagination.lastPage = response.data.meta.pagination.last_page || 1
            self.pagination.total = response.data.meta.pagination.total || 0
          }
        })
        .catch(function (error) {
          console.error('Error loading participants:', error)
          errorMessage('Falha ao carregar participantes')
        })
        .finally(function () {
          self.loading = false
        })
    },

    debouncedSearch: function () {
      var self = this
      clearTimeout(self.searchTimeout)
      self.searchTimeout = setTimeout(function () {
        self.pagination.currentPage = 1
        self.loadParticipants()
      }, 500)
    },

    changePage: function (page) {
      if (page < 1 || page > this.pagination.lastPage) return
      this.pagination.currentPage = page
      this.loadParticipants()
    },

    toggleSelectAll: function () {
      var self = this
      if (self.selectAll) {
        self.selectedParticipants = self.participants.map(function (p) { return p.id })
      } else {
        self.selectedParticipants = []
      }
    },

    getGroupName: function (participant) {
      if (participant.group && participant.group.data) {
        return participant.group.data.name
      }
      return '-'
    },

    printSingle: function (participant) {
      var self = this
      self.printingId = participant.id

      badgeServices.printBadge(participant.id)
        .then(function (response) {
          var badgeData = response.data || response
          if (!badgeData || !badgeData.template) {
            errorMessage('Erro ao carregar dados da etiqueta')
            return
          }
          self.executePrint(badgeData)
          self.markAsPrinted(participant.id)
        })
        .catch(function (error) {
          console.error('Print error:', error)
          var msg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Erro ao imprimir etiqueta'
          errorMessage(msg)
        })
        .finally(function () {
          self.printingId = null
        })
    },

    // Chunking sequencial pra evitar 429 do throttle.
    // Parte os IDs em chunks de 100, faz 1 POST /print-bulk por chunk em série,
    // e agrega os resultados num array único pronto pra executeBatchPrint.
    // Fail-fast: se um chunk falhar, aborta tudo (nenhuma etiqueta sai).
    printBulkInChunks: async function (participantIds) {
      var CHUNK_SIZE = 100
      var allItems = []
      this.loadProgress = { loaded: 0, total: participantIds.length }

      for (var i = 0; i < participantIds.length; i += CHUNK_SIZE) {
        var chunk = participantIds.slice(i, i + CHUNK_SIZE)
        var response = await badgeServices.printBulk(chunk)
        var payload = response.data || response
        var items = (payload && payload.items) || []

        for (var j = 0; j < items.length; j++) {
          if (items[j] && items[j].data && items[j].data.template) {
            allItems.push({
              participantId: items[j].participant_id,
              data: items[j].data
            })
          }
        }

        this.loadProgress = {
          loaded: Math.min(i + CHUNK_SIZE, participantIds.length),
          total: participantIds.length
        }
      }

      this.loadProgress = null
      return allItems
    },

    printSelected: async function () {
      var self = this
      if (self.selectedParticipants.length === 0) return

      self.loading = true
      var queue = self.selectedParticipants.slice()

      try {
        var badgeDataList = await self.printBulkInChunks(queue)

        if (badgeDataList.length > 0) {
          self.executeBatchPrint(badgeDataList)
        } else {
          errorMessage('Nenhuma etiqueta para imprimir')
        }
      } catch (error) {
        console.error('Erro ao carregar etiquetas em lote', error)
        var msg = (error.response && error.response.data && error.response.data.message)
          ? error.response.data.message
          : 'Erro ao carregar etiquetas'
        errorMessage(msg)
        self.loadProgress = null
      } finally {
        self.loading = false
      }
    },

    printAll: async function () {
      var self = this
      if (self.pagination.total === 0) return

      self.loadingAll = true

      var params = {
        status: 3,
        limit: 9999,
        include: 'group'
      }

      if (self.filters.search) {
        params.search = self.filters.search
      }
      if (self.filters.group_id) {
        params.group_id = self.filters.group_id
      }

      try {
        var listResponse = await participantServices.list(params)
        var allParticipants = (listResponse.data && listResponse.data.data) || []

        if (allParticipants.length === 0) {
          errorMessage('Nenhum participante para imprimir')
          return
        }

        var ids = allParticipants.map(function (p) { return p.id })
        var badgeDataList = await self.printBulkInChunks(ids)

        if (badgeDataList.length > 0) {
          self.executeBatchPrint(badgeDataList)
        } else {
          errorMessage('Nenhuma etiqueta para imprimir')
        }
      } catch (error) {
        console.error('Erro ao imprimir todos:', error)
        var msg = (error.response && error.response.data && error.response.data.message)
          ? error.response.data.message
          : 'Erro ao carregar participantes'
        errorMessage(msg)
        self.loadProgress = null
      } finally {
        self.loadingAll = false
      }
    },

    executeBatchPrint: function (badgeDataList) {
      var self = this
      console.log('Executando impressão em lote de', badgeDataList.length, 'etiquetas')

      // Pega o template do primeiro item
      var template = badgeDataList[0].data.template
      console.log('Template:', template)

      // Gera HTML com todas as etiquetas, cada uma em uma página
      var pagesHtml = badgeDataList.map(function (item, index) {
        var badgeHtml = self.generateBadgeHtml(item.data)
        // Adiciona page-break após cada etiqueta, exceto a última
        var pageBreak = index < badgeDataList.length - 1 ? 'page-break-after: always;' : ''
        return '<div class="badge-page" style="' + pageBreak + '">' + badgeHtml + '</div>'
      }).join('')

      console.log('HTML gerado, tamanho:', pagesHtml.length)

      // Usar nova janela para impressão em lote
      self.openPrintWindow(pagesHtml, template)
      console.log('Janela de impressão aberta')

      // Marca todos como impressos numa única chamada (em chunks de 500 se passar
      // do limite do endpoint).
      var ids = badgeDataList.map(function (item) { return item.participantId })
      self.markPrintedBulk(ids)

      successMessage('Impressão enviada! ' + badgeDataList.length + ' etiquetas.')
      self.selectedParticipants = []
      self.selectAll = false
      self.loadParticipants()
    },

    executePrint: function (badgeData) {
      var html = this.generateBadgeHtml(badgeData)
      var template = badgeData.template
      // Usar openPrintWindow diretamente (mais confiável)
      this.openPrintWindow(html, template)
    },

    isSelected: function (participantId) {
      return this.selectedParticipants.indexOf(participantId) > -1
    },

    toggleSelection: function (participantId) {
      var index = this.selectedParticipants.indexOf(participantId)
      if (index > -1) {
        this.selectedParticipants.splice(index, 1)
      } else {
        this.selectedParticipants.push(participantId)
      }
      // Atualizar selectAll
      this.selectAll = this.selectedParticipants.length === this.participants.length && this.participants.length > 0
    },

    openPrintWindow: function (html, template) {
      var styles = this.getBatchPrintStyles(template)

      var printWindow = window.open('', '_blank', 'width=800,height=600')
      if (!printWindow) {
        errorMessage('Popup bloqueado. Permita popups para imprimir.')
        return
      }

      // Script que aguarda todas as imagens carregarem antes de imprimir
      var printScript = [
        '<script>',
        'function waitForImages() {',
        '  var images = document.querySelectorAll("img");',
        '  var loaded = 0;',
        '  var total = images.length;',
        '  if (total === 0) { window.print(); return; }',
        '  images.forEach(function(img) {',
        '    if (img.complete) { loaded++; if (loaded === total) window.print(); }',
        '    else {',
        '      img.onload = function() { loaded++; if (loaded === total) window.print(); };',
        '      img.onerror = function() { loaded++; if (loaded === total) window.print(); };',
        '    }',
        '  });',
        '}',
        'window.onload = waitForImages;',
        '</' + 'script>'
      ].join('\n')

      var fullHtml = [
        '<!DOCTYPE html>',
        '<html><head>',
        '<meta charset="UTF-8">',
        '<title>Imprimir Etiquetas</title>',
        '<style>' + styles + '</style>',
        '</head><body>',
        html,
        printScript,
        '</body></html>'
      ].join('')

      printWindow.document.open()
      printWindow.document.write(fullHtml)
      printWindow.document.close()
    },

    getBatchPrintStyles: function (template) {
      var width = template.width || 90
      var height = template.height || 54

      return [
        '* { margin: 0; padding: 0; box-sizing: border-box; }',
        '@page { size: ' + width + 'mm ' + height + 'mm; margin: 0; }',
        'body { font-family: Arial, Helvetica, sans-serif; }',
        '.badge-page { width: ' + width + 'mm; height: ' + height + 'mm; }',
        '.badge { position: relative; width: ' + width + 'mm; height: ' + height + 'mm; overflow: hidden; }',
        '.badge-element { position: absolute; overflow: hidden; }',
        '.element-text { display: flex; align-items: center; }',
        '.element-image img, .element-qrcode img, .element-photo img { width: 100%; height: 100%; object-fit: contain; }'
      ].join(' ')
    },

    generateBadgeHtml: function (badgeData) {
      var template = badgeData.template
      var elements = badgeData.elements || []

      var bgColor = template.background_color || '#FFFFFF'
      var bgImage = template.background_image

      var bgStyle = 'background-color: ' + bgColor + ';'
      if (bgImage) {
        bgStyle += ' background-image: url(' + bgImage + '); background-size: cover; background-position: center;'
      }

      var elementsHtml = this.generateElementsHtml(elements, template.width, template.height)

      return '<div class="badge" style="' + bgStyle + '">' + elementsHtml + '</div>'
    },

    generateElementsHtml: function (elements, labelWidth, labelHeight) {
      var self = this
      var html = []

      elements.forEach(function (element) {
        if (element.is_visible === false) return
        var elementHtml = self.generateSingleElementHtml(element, labelWidth, labelHeight)
        if (elementHtml) {
          html.push(elementHtml)
        }
      })

      return html.join('')
    },

    generateSingleElementHtml: function (element, labelWidthMm, labelHeightMm) {
      var PX_TO_MM = 1 / 3.78
      var MARGIN_MM = 2

      var leftMm = (element.position_x || 0) * PX_TO_MM
      var topMm = (element.position_y || 0) * PX_TO_MM
      var elementWidthMm = (element.width || 20) * PX_TO_MM
      var elementHeightMm = (element.height || 10) * PX_TO_MM

      var availableWidthMm = Math.max(elementWidthMm, labelWidthMm - leftMm - MARGIN_MM)

      var baseStyles = [
        'position: absolute',
        'left: ' + leftMm.toFixed(2) + 'mm',
        'top: ' + topMm.toFixed(2) + 'mm',
        'z-index: ' + (element.z_index || 1)
      ]

      if (element.rotation) {
        baseStyles.push('transform: rotate(' + element.rotation + 'deg)')
      }

      var content = ''
      var additionalStyles = []
      var elementClass = 'badge-element'

      switch (element.type) {
        case 'text':
        case 'dynamic_field':
          elementClass += ' element-text'
          var textValue = element.value || element.content || ''

          // Se for campo de nome, aplicar lógica inteligente
          if (this.isNameField(element) && textValue) {
            textValue = this.getSmartNameValue(element, textValue, availableWidthMm, elementHeightMm)
          }

          // Usa espaço disponível para calcular fit e definir dimensões
          additionalStyles = this.getTextStyles(element, textValue, availableWidthMm.toFixed(2), elementHeightMm.toFixed(2))
          content = this.escapeHtml(textValue)
          baseStyles.push('width: ' + availableWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'image':
          elementClass += ' element-image'
          var imgSrc = element.content || ''
          if (imgSrc) {
            content = '<img src="' + imgSrc + '" />'
          }
          baseStyles.push('width: ' + elementWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'qrcode':
          elementClass += ' element-qrcode'
          var qrcodeSrc = element.content || ''
          if (qrcodeSrc) {
            content = '<img src="' + qrcodeSrc + '" />'
          }
          baseStyles.push('width: ' + elementWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'photo':
          elementClass += ' element-photo'
          if (element.content) {
            content = '<img src="' + element.content + '" style="width:100%;height:100%;object-fit:cover;" />'
          } else {
            content = '<div style="width:100%;height:100%;background:#f0f0f0;display:flex;align-items:center;justify-content:center;font-size:10px;color:#999;">Foto</div>'
          }
          baseStyles.push('width: ' + elementWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        case 'shape':
          additionalStyles = this.getShapeStyles(element)
          baseStyles.push('width: ' + elementWidthMm.toFixed(2) + 'mm')
          baseStyles.push('height: ' + elementHeightMm.toFixed(2) + 'mm')
          break

        default:
          return ''
      }

      var allStyles = baseStyles.concat(additionalStyles).join('; ')
      return '<div class="' + elementClass + '" style="' + allStyles + '">' + content + '</div>'
    },

    getTextStyles: function (element, content, elementWidthMm, elementHeightMm) {
      var styles = element.styles || {}
      var widthMm = parseFloat(elementWidthMm)
      var heightMm = parseFloat(elementHeightMm)

      var fontSizePx = styles.font_size || 12
      var fontFamily = styles.font_family || 'Arial, sans-serif'
      var fontWeight = styles.font_bold ? 'bold' : 'normal'
      var fontStyle = styles.font_italic ? 'italic' : 'normal'
      var textAlign = styles.text_align || 'left'

      // Usar binary search para encontrar o tamanho ótimo
      var fitResult = this.findOptimalFontSize(content, fontSizePx, widthMm, heightMm, fontFamily, fontWeight)

      var textStyles = [
        'display: flex',
        'flex-direction: column',
        'font-family: ' + fontFamily,
        'font-size: ' + fitResult.fontSize + 'mm',
        'line-height: ' + fitResult.lineHeight,
        'color: ' + (styles.font_color || '#000000'),
        'font-weight: ' + fontWeight,
        'font-style: ' + fontStyle,
        'overflow: hidden'
      ]

      if (fitResult.shouldWrap) {
        textStyles.push('white-space: normal')
        textStyles.push('word-wrap: break-word')
        textStyles.push('justify-content: center')
      } else {
        textStyles.push('white-space: nowrap')
        textStyles.push('justify-content: center')
        textStyles.push('text-overflow: ellipsis')
      }

      // Alinhamento horizontal
      var alignItems = 'flex-start'
      if (textAlign === 'center') alignItems = 'center'
      else if (textAlign === 'right') alignItems = 'flex-end'
      textStyles.push('align-items: ' + alignItems)
      textStyles.push('text-align: ' + textAlign)

      return textStyles
    },

    findOptimalFontSize: function (content, baseFontSizePx, widthMm, heightMm, fontFamily, fontWeight) {
      var PX_TO_MM = 1 / 3.78
      var MM_TO_PX = 3.78
      var baseFontSizeMm = baseFontSizePx * PX_TO_MM
      var LINE_HEIGHT = 1.2

      var result = {
        fontSize: baseFontSizeMm.toFixed(2),
        shouldWrap: false,
        lineHeight: LINE_HEIGHT.toString()
      }

      if (!content || content.length === 0) {
        return result
      }

      // Converter dimensões para pixels para medição
      var containerWidthPx = widthMm * MM_TO_PX
      var containerHeightPx = heightMm * MM_TO_PX

      // Criar elemento de medição invisível
      var measureEl = document.createElement('div')
      measureEl.style.cssText = [
        'position: absolute',
        'visibility: hidden',
        'left: -9999px',
        'top: -9999px',
        'font-family: ' + fontFamily,
        'font-weight: ' + fontWeight,
        'line-height: ' + LINE_HEIGHT,
        'padding: 0',
        'margin: 0',
        'border: 0'
      ].join(';')
      document.body.appendChild(measureEl)

      try {
        // PASSO 1: Testar fonte original em linha única
        measureEl.style.whiteSpace = 'nowrap'
        measureEl.style.width = 'auto'
        measureEl.style.fontSize = baseFontSizePx + 'px'
        measureEl.textContent = content

        var singleLineWidth = measureEl.scrollWidth
        var singleLineHeight = measureEl.scrollHeight

        // Se cabe em linha única com fonte original - perfeito!
        if (singleLineWidth <= containerWidthPx && singleLineHeight <= containerHeightPx) {
          return result
        }

        // PASSO 2: Testar fonte original COM wrap (quebra de linha)
        measureEl.style.whiteSpace = 'normal'
        measureEl.style.wordWrap = 'break-word'
        measureEl.style.width = containerWidthPx + 'px'

        var wrappedHeight = measureEl.scrollHeight

        // Se cabe com wrap na fonte original - usar wrap!
        if (wrappedHeight <= containerHeightPx) {
          result.shouldWrap = true
          return result
        }

        // PASSO 3: Binary search para encontrar maior fonte que cabe com wrap
        var MIN_FONT_SIZE_MM = 2
        var low = MIN_FONT_SIZE_MM * MM_TO_PX
        var high = baseFontSizePx
        var bestFit = low
        var maxIterations = 10

        for (var i = 0; i < maxIterations && low <= high; i++) {
          var mid = Math.floor((low + high) / 2)

          measureEl.style.fontSize = mid + 'px'
          var testHeight = measureEl.scrollHeight

          if (testHeight <= containerHeightPx) {
            bestFit = mid
            low = mid + 1
          } else {
            high = mid - 1
          }
        }

        result.fontSize = (bestFit * PX_TO_MM).toFixed(2)
        result.shouldWrap = true
        return result
      } finally {
        // Sempre remover elemento de medição
        document.body.removeChild(measureEl)
      }
    },

    getShapeStyles: function (element) {
      var styles = element.styles || {}
      var shapeStyles = [
        'background-color: ' + (styles.background_color || 'transparent')
      ]

      if (styles.border_width && styles.border_width > 0) {
        shapeStyles.push('border: ' + styles.border_width + 'px solid ' + (styles.border_color || '#000000'))
      }

      var shapeType = styles.shape_type || 'rectangle'
      if (shapeType === 'circle' || shapeType === 'ellipse') {
        shapeStyles.push('border-radius: 50%')
      } else if (styles.border_radius) {
        shapeStyles.push('border-radius: ' + styles.border_radius + 'px')
      }

      return shapeStyles
    },

    isNameField: function (element) {
      if (element.type !== 'dynamic_field') return false
      var fieldName = element.standard_field_name || element.field_key || ''
      return fieldName === 'name' || fieldName === 'badge_name'
    },

    getFirstAndLastName: function (fullName) {
      if (!fullName || typeof fullName !== 'string') return fullName || ''

      var parts = fullName.trim().split(/\s+/)

      if (parts.length <= 2) {
        return fullName.trim()
      }

      var firstName = parts[0]
      var lastName = parts[parts.length - 1]

      return firstName + ' ' + lastName
    },

    getSmartNameValue: function (element, originalValue, widthMm, heightMm) {
      if (!originalValue || typeof originalValue !== 'string') {
        return originalValue || ''
      }

      var parts = originalValue.trim().split(/\s+/)
      var MAX_CHARS = 20

      // Se tem mais de 2 palavras E é muito longo, encurta
      if (parts.length > 2 && originalValue.length > MAX_CHARS) {
        return this.getFirstAndLastName(originalValue)
      }

      return originalValue
    },

    escapeHtml: function (text) {
      if (text === null || text === undefined) return ''
      var str = String(text)
      var div = document.createElement('div')
      div.textContent = str
      return div.innerHTML
    },

    markAsPrinted: function (participantId) {
      // Single — usa o bulk com 1 ID pra ter um caminho só.
      this.markPrintedBulk([participantId])
    },

    // Marca em massa. Quebra em chunks de 500 (limite do endpoint) e dispara
    // sequencialmente. Fire-and-forget — não bloqueia o usuário, só loga erro.
    markPrintedBulk: async function (participantIds) {
      var CHUNK_SIZE = 500
      try {
        for (var i = 0; i < participantIds.length; i += CHUNK_SIZE) {
          var chunk = participantIds.slice(i, i + CHUNK_SIZE)
          await badgeServices.markPrintedBulk(chunk)
        }
      } catch (error) {
        console.error('Error marking as printed (bulk):', error)
      }
    }
  }
}
</script>

<style scoped>
.link { cursor: pointer; }
.table-active {
  background-color: #e3f2fd !important;
}
</style>
